import React, { useEffect, useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import AlertsBell from "./AlertsBell";
import GuidedTourButton from "./GuidedTourButton";
import WelcomeTourAutoLaunch from "./WelcomeTourAutoLaunch";
import NavGuard from "./NavGuard";
import TenantSwitcher from "./TenantSwitcher";
import api from "../lib/api";
import {
  Menu, X, Home, Network, Users, CalendarDays, Receipt, FileText, Inbox, UserCircle2, Settings,
  Briefcase, Sparkles, UserMinus, Plane, Wallet, TrendingUp, GraduationCap, BookOpen, CalendarClock, Building2, Grid3x3, LifeBuoy, BarChart3, Handshake,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const MobileLink = ({ to, icon: Icon, label, onClick, testid }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={testid ? `${testid}-mobile` : undefined}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-[12px] text-[14px] font-medium transition-all ${
        isActive ? "bg-[#e2e8f0] text-[#1e293b]" : "text-[#475569] hover:bg-[#f1f5f9]"
      }`
    }
  >
    <Icon size={17} strokeWidth={1.75} className="shrink-0 text-[#94a3b8]" />
    <span className="flex-1 truncate">{label}</span>
  </NavLink>
);

const MobileSection = ({ children }) => (
  <div className="px-4 pt-4 pb-1 text-[10px] font-semibold tracking-[0.12em] uppercase text-[#94a3b8]">{children}</div>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const isAdmin = user && ["admin", "hr"].includes(user.role);
  const canHire = user && ["admin", "hr", "manager", "ceo"].includes(user.role);
  const [hasLoans, setHasLoans] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (["hr", "admin"].includes(user.role)) { setHasLoans(true); return; }
    api.get("/loans?mine=true")
      .then((r) => setHasLoans(Array.isArray(r.data) && r.data.length > 0))
      .catch(() => setHasLoans(false));
  }, [user]);

  const close = () => setOpen(false);

  return (
    <div className="min-h-screen flex bg-[#f1f5f9]">
      <Sidebar />

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-[#e2e8f0] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo variant="mark" className="w-9 h-9 shrink-0" />
          <span className="font-semibold text-[#1e293b] tracking-wide">Welcome Home</span>
        </div>
        <div className="flex items-center gap-2">
          <AlertsBell />
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="text-[#1e293b] p-1.5 rounded-[10px] hover:bg-[#f1f5f9]">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 top-[57px] z-20 bg-white overflow-y-auto">
          <div className="flex flex-col gap-0.5 pb-6">
            <MobileSection>{t("nav.section.workspace") || "Workspace"}</MobileSection>
            <MobileLink to="/app/dashboard" icon={Home} label={t("nav.dashboard")} onClick={close} testid="nav-dashboard" />
            <MobileLink to="/app/planner" icon={CalendarClock} label="Planner" onClick={close} testid="nav-planner" />
            <MobileLink to="/app/pto" icon={CalendarDays} label={t("nav.pto")} onClick={close} testid="nav-pto" />
            <MobileLink to="/app/travel" icon={Plane} label={t("travel.page_title")} onClick={close} testid="nav-travel" />
            <MobileLink to="/app/reimbursements" icon={Receipt} label={t("nav.reimb")} onClick={close} testid="nav-reimb" />
            <MobileLink to="/app/trainings" icon={GraduationCap} label="Trainings" onClick={close} testid="nav-trainings" />

            <MobileSection>{t("nav.section.people") || "People"}</MobileSection>
            <MobileLink to="/app/directory" icon={Users} label={t("nav.directory")} onClick={close} testid="nav-directory" />
            <MobileLink to="/app/departments" icon={Building2} label={t("nav.departments")} onClick={close} testid="nav-departments" />
            <MobileLink to="/app/org-chart" icon={Network} label={t("nav.org")} onClick={close} testid="nav-org" />
            <MobileLink to="/app/performance" icon={TrendingUp} label="Performance" onClick={close} testid="nav-performance" />
            {canHire && <MobileLink to="/app/hiring" icon={Briefcase} label="Hiring" onClick={close} testid="nav-hiring" />}
            {canHire && <MobileLink to="/app/offboarding" icon={UserMinus} label="Offboarding" onClick={close} testid="nav-offboarding" />}
            {(isAdmin || user?.role === "ceo") && <MobileLink to="/app/salary-matrix" icon={Grid3x3} label={t("nav.salary_matrix")} onClick={close} testid="nav-salary" />}

            <MobileSection>{t("nav.section.operations") || "Operations"}</MobileSection>
            <MobileLink to="/app/approvals" icon={Inbox} label={t("nav.approvals")} onClick={close} testid="nav-approvals" />
            <MobileLink to="/app/reports" icon={BarChart3} label={t("nav.reports")} onClick={close} testid="nav-reports" />
            {hasLoans && <MobileLink to="/app/loans" icon={Wallet} label={t("nav.loans")} onClick={close} testid="nav-loans" />}
            <MobileLink to="/app/documents" icon={FileText} label={t("nav.docs")} onClick={close} testid="nav-docs" />
            {canHire && <MobileLink to="/app/knowledge" icon={BookOpen} label="Knowledge" onClick={close} testid="nav-knowledge" />}

            <MobileSection>{t("nav.section.system") || "System"}</MobileSection>
            <MobileLink to="/app/copilot" icon={Sparkles} label="Sage Copilot" onClick={close} testid="nav-copilot" />
            <MobileLink to="/app/manual" icon={LifeBuoy} label={t("nav.manual") || "User manual"} onClick={close} testid="nav-manual" />
            <MobileLink to="/app/profile" icon={UserCircle2} label={t("nav.profile")} onClick={close} testid="nav-profile" />
            {isAdmin && <MobileLink to="/app/service-contracts" icon={Handshake} label={t("nav.service_contracts")} onClick={close} testid="nav-service-contracts" />}
            {isAdmin && <MobileLink to="/app/admin" icon={Settings} label={t("nav.admin")} onClick={close} testid="nav-admin" />}

            <div className="mt-4 px-4">
              <TenantSwitcher />
            </div>
            <div className="mt-4 px-4 flex items-center justify-between">
              <LanguageToggle />
              <button onClick={logout} className="sw-btn-secondary">{t("nav.logout")}</button>
            </div>
          </div>
        </div>
      )}

      <main className="relative flex-1 min-w-0 pt-[60px] lg:pt-0">
        <div className="hidden lg:flex sticky top-0 z-30 items-center justify-end gap-3 px-8 py-4 bg-[#f1f5f9]/80 backdrop-blur-sm border-b border-transparent">
          <PageTour />
          <AlertsBell />
        </div>
        <div className="max-w-[1400px] mx-auto p-6 lg:px-10 lg:pt-2 lg:pb-10">
          <NavGuard>
            <Outlet />
          </NavGuard>
        </div>
      </main>
      <WelcomeTourAutoLaunch />
    </div>
  );
}

function PageTour() {
  const loc = useLocation();
  // Path format: /app/<key>[/...]
  const parts = loc.pathname.split("/").filter(Boolean);
  const pageKey = parts[0] === "app" ? (parts[1] || "dashboard") : null;
  if (!pageKey) return null;
  return <GuidedTourButton pageKey={pageKey} />;
}
